import { HistoryIcon } from '@/components/icons/history-icon'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { TableMetadataVHAPIResponse } from '@/hooks/api/use-table-metadata-version-history-query'
import React from 'react'
import dayjs from 'dayjs'
import { T } from '@tolgee/react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { DialogDescription, DialogTitle } from '@/components/ui/dialog'

type CollapsibleVersionItemProps = {
  item: {
    timestamp: string
    changedBy: string
    summary: string[]
  }
}

function CollapsibleVersionItem({ item }: CollapsibleVersionItemProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-1'>
          <CollapsibleTrigger asChild>
            <Button size={'sm'} unstyled>
              {isOpen ? (
                <ChevronDownIcon className='text-neutral-200' />
              ) : (
                <ChevronRightIcon className='text-neutral-200' />
              )}
            </Button>
          </CollapsibleTrigger>
          <span className='text-xs font-[450] text-neutral-500'>{item.timestamp}</span>
        </div>
        {item?.changedBy && (
          <div className='flex items-center gap-1 pl-5'>
            <div className='m-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mango-300'>
              <span className='pt-0.5 font-neue-machina text-xs font-normal capitalize text-neutral-white'>
                {item.changedBy?.[0]}
              </span>
            </div>
            <span className='line-clamp-1 text-xs font-light text-neutral-500'>
              {item.changedBy}
            </span>
          </div>
        )}
        <CollapsibleContent className='pl-7'>
          <p className='text-[10px] font-light text-neutral-300'>
            <T keyName='metadata.versionHistory.summary' />
          </p>
          <ul className='list-disc pl-4'>
            {item?.summary.map((s: string) => (
              <li key={s} className='text-[10px] font-light text-neutral-300'>
                {s}
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

type TableMetadataVersionHistoryProps = {
  tableVHData?: TableMetadataVHAPIResponse
  currentTableVersion?: string
  tableVersion: string
  setTableVersion: React.Dispatch<React.SetStateAction<string>>
}

export function TableMetadataVersionHistory(props: TableMetadataVersionHistoryProps) {
  const { tableVHData, tableVersion, setTableVersion, currentTableVersion } = props

  const groupedVHData = React.useMemo(() => {
    if (!tableVHData) return {}

    const result: Record<string, any[]> = {}

    tableVHData?.version_history?.forEach((vh) => {
      const timestamp = dayjs(vh?.timestamp_unix)
      const monthYear = timestamp.format('MMMM, YYYY')

      if (!result[monthYear]) {
        result[monthYear] = []
      }
      const summary: string[] = []
      vh?.change_description?.fields_added?.forEach((field) => {
        summary.push(`${field?.field_name} Added`)
      })
      vh?.change_description?.fields_updated?.forEach((field) => {
        summary.push(`${field?.field_name} Updated`)
      })
      vh?.change_description?.fields_deleted?.forEach((field) => {
        summary.push(`${field?.field_name} Deleted`)
      })
      result[monthYear].push({
        timestamp: timestamp.format('MM-DD-YYYY, hh:mm:ss A'),
        changedBy: vh.changed_by?.[0] || '',
        version: vh?.version,
        summary,
      })
    })

    return result
  }, [tableVHData?.version_history])

  return (
    <div className='flex items-center gap-4'>
      <Sheet modal={false}>
        <SheetTrigger asChild>
          <TooltipIconButton
            keyName='metadata.versionHistory.title'
            contentProps={{ side: 'bottom' }}
            className='size-8 rounded-[4px] hover:text-mango-300 focus:text-mango-300 active:text-mango-300'
          >
            <HistoryIcon className='shrink-0' />
          </TooltipIconButton>
        </SheetTrigger>
        <SheetContent
          className='left-16 top-[92px] overflow-y-auto rounded-br-lg rounded-tr-lg bg-neutral-white p-8 pb-[116px] shadow-sheet'
          side='left'
        >
          <DialogTitle className='font-semibold'>{tableVHData?.table_name}</DialogTitle>
          <DialogDescription className='hidden' />
          <div>
            {Object.keys(groupedVHData).map((formattedTimestamp) => {
              return (
                <div key={formattedTimestamp}>
                  <p className='text-xs font-[450] text-neutral-500'>{formattedTimestamp}</p>
                  <div className='flex flex-col gap-2 py-5'>
                    {groupedVHData[formattedTimestamp].map((item) => (
                      <div
                        key={item.timestamp}
                        className={cn('flex gap-4 p-4', {
                          'rounded-xl bg-mango-50': tableVersion === item?.version,
                        })}
                      >
                        <div className='flex w-12 flex-col items-start gap-0.5 font-[450] text-mango-300'>
                          <Button
                            className='hover:underline'
                            onClick={() => setTableVersion(item?.version)}
                            unstyled
                          >
                            {item?.version}
                          </Button>
                          {currentTableVersion === item.version && (
                            <span className='text-xs'>
                              <T keyName='metadata.versionHistory.current' />
                            </span>
                          )}
                        </div>
                        <CollapsibleVersionItem item={item} />
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </SheetContent>
      </Sheet>
      <div className='rounded-full bg-mango-50 px-4 py-3 font-[450] leading-none text-mango-300'>
        <T keyName='metadata.versionHistory.version' params={{ tableVersion }} />{' '}
        {tableVersion === currentTableVersion && <T keyName='metadata.versionHistory.current' />}
      </div>
    </div>
  )
}
